import { Bot, ChevronDown, Phone } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CallRecordingPlayer, formatTimestamp } from './call-recording-player';
import { CallTranscript } from './call-transcript';
import { MessageAvatarSpacer, MessageHandlers } from './message-bubble';
import { useCallRecording } from './use-call-recording';
import type { CallActivity } from './types';
import { formatDateTime } from '~/lib/dates';
import { cn } from '~/lib/utils';

export function CallActivityMessage({ activity }: { activity: CallActivity }) {
  const { t } = useTranslation('tasks');
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const {
    currentTime,
    duration,
    progress,
    isPlaying,
    isSeekable,
    hasError,
    togglePlay,
    seek,
    playFrom,
  } = useCallRecording(activity);
  const isMissed =
    activity.outcome === 'missed' || activity.outcome === 'unanswered';
  const isOutbound = activity.direction === 'outbound';
  const name = activity.callerName;
  const showPlayer = !activity.isInProgress && activity.recordingUrl !== null;
  // Independent of the recording: a call can carry a transcript whose audio was
  // never stored. Click-to-seek is inert without a player.
  const showTranscript =
    !activity.isInProgress && activity.transcription !== null;

  return (
    <article
      className={cn('flex', isOutbound ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'flex w-full max-w-bubble flex-col gap-2.5',
          isOutbound ? 'items-end' : 'items-start',
        )}
      >
        {/* Two rows, each behind an avatar-width column: the avatars belong to
            the card, and the transcript below has to start where the card does.
            One column shared by both is what keeps those two edges equal. */}
        <div
          className={cn(
            'flex w-full min-w-0 items-end gap-1.5',
            isOutbound && 'flex-row-reverse',
          )}
        >
          <MessageHandlers
            handledBy={activity.handledBy}
            isOutbound={isOutbound}
          />
          <div
            className={cn(
              // One width whatever the outcome — a row of calls should not step
              // in and out as some were answered and some were not.
              'flex w-75 max-w-full flex-col gap-2 rounded-2xl p-3',
              isMissed
                ? 'bg-destructive-subtle text-destructive-subtle-foreground'
                : 'bg-primary-subtle text-primary',
            )}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">
                {isMissed
                  ? t('Missed call')
                  : activity.isInProgress
                    ? t('Call in progress')
                    : t('Call ended')}
              </p>
              <p className="truncate text-xs">
                {isOutbound
                  ? t('To {{name}}', { name })
                  : t('From {{name}}', { name })}
              </p>
            </div>
            {showPlayer && (
              <CallRecordingPlayer
                isPlaying={isPlaying}
                progress={progress}
                togglePlay={togglePlay}
                onSeekRatio={(ratio) => seek(ratio * duration)}
                disabled={hasError}
                seekDisabled={!isSeekable}
              />
            )}
            <CallActivityFooter
              activity={activity}
              isMissed={isMissed}
              currentTime={currentTime}
              isPlaying={isPlaying}
            />
          </div>
        </div>
        {showTranscript && (
          <div
            className={cn(
              'flex w-full gap-1.5',
              isOutbound && 'flex-row-reverse',
            )}
          >
            <MessageAvatarSpacer />
            <div className="min-w-0 flex-1">
              <CallTranscriptSection
                isOpen={isTranscriptOpen}
                isOutbound={isOutbound}
                onToggle={() => setIsTranscriptOpen((open) => !open)}
              >
                <CallTranscript
                  callId={activity.callId}
                  transcription={activity.transcription}
                  speakerTimeline={activity.speakerTimeline}
                  currentTime={currentTime}
                  onSeek={playFrom}
                />
              </CallTranscriptSection>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Duration on the left, channel on the right. While the recording plays the
 * left side counts up instead — the design asks for it to read like a voice
 * message rather than a fixed length.
 */
function CallActivityFooter({
  activity,
  isMissed,
  currentTime,
  isPlaying,
}: {
  activity: CallActivity;
  isMissed: boolean;
  currentTime: number;
  isPlaying: boolean;
}) {
  const { t } = useTranslation('tasks');
  const isStarted = isPlaying || currentTime > 0;
  /** What a missed call "lasted" is how long it rang, which is not a length
   *  anybody wants read back to them. */
  const durationSeconds =
    activity.isInProgress || isMissed ? null : activity.durationSeconds;
  const elapsed = isStarted
    ? formatTimestamp(currentTime)
    : durationSeconds !== null && durationSeconds > 0
      ? formatTimestamp(durationSeconds)
      : null;

  return (
    <div className="flex items-center justify-between gap-2 text-2xs">
      <span className="flex items-center gap-1.5">
        {activity.isInProgress && (
          <span
            aria-hidden
            className="size-2 animate-pulse rounded-full bg-current"
          />
        )}
        {elapsed && <span className="tabular-nums">{elapsed}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-1 opacity-80">
        {activity.hadAi && (
          <>
            <Bot className="size-3" strokeWidth={2.5} />
            <span>{t('Assistant')}</span>
            <span>·</span>
          </>
        )}
        <Phone className="size-3" strokeWidth={2.5} />
        <span>{formatDateTime(activity.createdAt)}</span>
      </span>
    </div>
  );
}

/**
 * The toggle sits on a rule when closed and straddles the panel's bottom edge
 * when open — the same line either way, so opening reads as the panel growing
 * out of it rather than a second thing appearing.
 */
function CallTranscriptSection({
  isOpen,
  isOutbound,
  onToggle,
  children,
}: {
  isOpen: boolean;
  isOutbound: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('tasks');
  const toggle = (
    <button
      type="button"
      aria-expanded={isOpen}
      onClick={onToggle}
      className="flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-full bg-background px-3 text-xs text-muted-foreground hover:text-foreground"
    >
      {t('Call transcript')}
      <ChevronDown
        className={cn('size-4 transition-transform', isOpen && 'rotate-180')}
        strokeWidth={2.5}
      />
    </button>
  );

  if (!isOpen) {
    return (
      <div
        className={cn(
          'flex w-full items-center',
          isOutbound && 'flex-row-reverse',
        )}
      >
        {toggle}
      </div>
    );
  }

  return (
    <div className="relative w-full pb-5">
      <div className="rounded-2xl border border-border bg-card p-5">
        {children}
      </div>
      <div
        className={cn('absolute bottom-0', isOutbound ? 'right-6' : 'left-6')}
      >
        {toggle}
      </div>
    </div>
  );
}
